import { makeAutoObservable, runInAction } from "mobx";
import type { ApiClient } from "@/clients/ApiClient";
import type { HistoryCleared, HistoryResponse, ListeningEntry } from "@/generated/api";

type HistoryApi = Pick<ApiClient, "get" | "put" | "delete">;

export type HistoryFilter = "all" | "started" | "played";

const PAGE = 30;

/**
 * The listener's listening history on the account: a page at a time, filtered, with the counts of the whole
 * history. Changes go to the API first and the list follows, so what is shown is what the account holds.
 */
export class HistoryStore {
    entries: ListeningEntry[] = [];
    counts = { started: 0, played: 0 };
    filter: HistoryFilter = "all";
    nextCursor = "";
    loading = false;
    loaded = false;
    failed = false;
    private generation = 0;

    constructor(private readonly client: HistoryApi) {
        makeAutoObservable<HistoryStore, "client" | "generation">(this, { client: false, generation: false });
    }

    /** Shows the first page of a filter */
    async load(filter: HistoryFilter = this.filter) {
        const generation = ++this.generation;
        runInAction(() => {
            this.filter = filter;
            this.loading = true;
            this.failed = false;
        });
        try {
            const page = await this.fetch("");
            if (generation !== this.generation) return;
            runInAction(() => {
                this.entries = page.entries;
                this.apply(page);
            });
        } catch {
            if (generation === this.generation) runInAction(() => (this.failed = true));
        } finally {
            if (generation === this.generation) runInAction(() => ((this.loading = false), (this.loaded = true)));
        }
    }

    /** Adds the next page */
    async more() {
        if (!this.nextCursor || this.loading) return;
        const generation = this.generation;
        runInAction(() => (this.loading = true));
        try {
            const page = await this.fetch(this.nextCursor);
            if (generation !== this.generation) return;
            runInAction(() => {
                this.entries = [...this.entries, ...page.entries];
                this.apply(page);
            });
        } catch {
            if (generation === this.generation) runInAction(() => (this.failed = true));
        } finally {
            if (generation === this.generation) runInAction(() => (this.loading = false));
        }
    }

    async markPlayed(episodeId: number) {
        await this.client.put(`/me/listening/${episodeId}/played`, {});
        await this.load();
    }

    async markUnplayed(episodeId: number) {
        await this.client.put(`/me/listening/${episodeId}/unplayed`, {});
        await this.load();
    }

    /** Forgets all progress, or only that of one podcast. Answers how many entries went. */
    async clear(podcastId?: number): Promise<number> {
        const result = await this.client.delete<HistoryCleared>(
            podcastId ? `/me/history?podcast=${podcastId}` : "/me/history"
        );
        await this.load();
        return result?.removed ?? 0;
    }

    reset() {
        this.generation += 1;
        this.entries = [];
        this.counts = { started: 0, played: 0 };
        this.nextCursor = "";
        this.loaded = false;
        this.loading = false;
        this.failed = false;
    }

    private fetch(cursor: string): Promise<HistoryResponse> {
        const query = new URLSearchParams({ limit: String(PAGE) });
        if (this.filter !== "all") query.set("filter", this.filter);
        if (cursor) query.set("cursor", cursor);
        return this.client.get<HistoryResponse>(`/me/history?${query}`);
    }

    private apply(page: HistoryResponse) {
        this.nextCursor = page.next_cursor;
        this.counts = page.counts;
    }
}
